import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import SerialNumberBadge from './SerialNumberBadge';
import OrderStatusBadge from '../order/OrderStatusBadge';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import './ProductDetailsModal.css';
import { RiEdit2Line,RiToolsLine } from "react-icons/ri"; // Keep other icons


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatDateTime = (dateString) => {
    if (!dateString) return 'Not available';
    
    try {
      return new Date(dateString).toLocaleString('en-US', {
        timeZone: 'Asia/Jerusalem',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'Invalid date';
    }
  };

const ProductDetailsModal = ({ productId, onClose }) => {
    const [product, setProduct] = useState(null);
    const [relatedOrders, setRelatedOrders] = useState([]);
    const [category, setCategory] = useState(null);
    const [warehouse, setWarehouse] = useState(null);
    const [department, setDepartment] = useState(null);
    const [relatedFaultyItems, setRelatedFaultyItems] = useState([]);
    const [missingItems, setMissingItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate(); // Initialize useNavigate
    const overlayRef = useRef(null);

    const fetchProductDetails = useCallback(async () => {
        if (!productId) return;
        setIsLoading(true);
        try {
            const [productResponse, ordersResponse] = await Promise.all([
                axios.get(`${BACKEND_URL}/api/products/${productId}`),
                axios.get(`${BACKEND_URL}/api/orders/getorders`),
            ]);

            const productData = productResponse.data;
            setProduct(productData);

            // Fetch category and warehouse details
            const [categoryResponse, warehouseResponse, departmentResponse] = await Promise.all([
                axios.get(`${BACKEND_URL}/api/categories/${productData.category}`),
                axios.get(`${BACKEND_URL}/api/warehouses/${productData.warehouse}`),
                axios.get(`${BACKEND_URL}/api/departments/${productData.department}`),
            ]);
            // Fetch related faulty items
            const faultyItemsResponse = await axios.get(`${BACKEND_URL}/api/faulty-items/by-product/${productId}`);
            setRelatedFaultyItems(faultyItemsResponse.data);

            setCategory(categoryResponse.data);
            setWarehouse(warehouseResponse.data);
            setDepartment(departmentResponse.data);
            // Filter orders that contain this product and are either "committed" or "issued" (excluding partially_returned)
            const relevantOrders = ordersResponse.data.filter(order => 
                order.products.some(p => p.product === productId) &&
                (order.orderStatus === 'committed' || order.orderStatus === 'issued')
            );
            setRelatedOrders(relevantOrders);

            // Extract missing items from all orders that contain this product
            const missingItemsData = [];
            for (const order of ordersResponse.data) {
                const productInOrder = order.products.find(p => p.product === productId);
                if (productInOrder && (productInOrder.missingQuantity > 0 || (productInOrder.missingSerialNumbers && productInOrder.missingSerialNumbers.length > 0))) {
                    // Fetch user information
                    let userName = 'Unknown';
                    if (order.requestedBy) {
                        try {
                            const userResponse = await axios.get(`${BACKEND_URL}/api/users/${order.requestedBy}`);
                            userName = userResponse.data.name || userResponse.data.email;
                        } catch (error) {
                            console.error('Failed to fetch user info:', error);
                        }
                    }

                    missingItemsData.push({
                        orderNumber: order.orderNumber,
                        orderName: order.orderName,
                        userName: userName,
                        destination: order.destination,
                        fromDate: order.fromDate,
                        untilDate: order.untilDate,
                        missingQuantity: productInOrder.missingQuantity || 0,
                        missingSerialNumbers: productInOrder.missingSerialNumbers || [],
                        managedBySerial: productData.managedBySerial
                    });
                }
            }
            setMissingItems(missingItemsData);
        } catch (error) {
            console.error('Error fetching product details:', error);
            toast.error('Failed to fetch product information');
        } finally {
            setIsLoading(false);
        }
    }, [productId, setIsLoading, setProduct, setCategory, setWarehouse, setDepartment, setRelatedFaultyItems, setRelatedOrders]);

    useEffect(() => {
        if (productId) {
            fetchProductDetails();
        }
    }, [productId, fetchProductDetails]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (overlayRef.current && overlayRef.current === event.target) {
                onClose();
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [onClose]);

    if (!product || isLoading) return <div className="loader">Loading...</div>;
    console.log(product);

    const handleGoToUpdatePage = () => {
        if (product) {
            onClose(); // Close the modal before navigating
            navigate('/update-product', { state: { product } });
        }
    };

    const handleReportFaulty = () => {
        if (product) {
            onClose(); // Close the modal before navigating
            navigate('/CreateFaultyItem', { state: { product } });
        }
    };

        const handleEditFaultyItem = (item) => {
        if (item) {
            onClose(); // Close the modal before navigating
            navigate(`/EditFaultyItem`, { state: { faultyItemData: item } });
        }
    };

    const handleMarkAsRepaired = async (itemId) => {
        try {
        await axios.put(`${BACKEND_URL}/api/faulty-items/${itemId}/repair`);
        toast.success(`Item marked as repaired and returned to inventory.`);
        fetchProductDetails();
        } catch (error) {
        console.error('Failed to mark item as repaired:', error);
        toast.error(error.response?.data?.message || 'Failed to mark item as repaired.');
        }
  };



    return (
        <div className="modal-overlay" ref={overlayRef}>
            <div className="modal-content">
                <button className="modal-close" onClick={onClose}>×</button>

                {/* Header Section */}
                <div className="modal-header">
                    <div className="product-basic-info">
                        <h2>{product.productName}</h2>
                        <p className="product-code">{product.productCode}</p>
                    </div>
                </div>

                {/* Main Product Information */}
                <div className="product-details-grid">
                    <div className="info-section">
                        <h3>General Information</h3>
                        <button
                        onClick={handleReportFaulty}
                        className="btn btn-sm btn-outline-warning report-faulty-item-button"
                        title="Report a faulty item for this product"
                    >
                        ⚠️ Report Faulty Item
                        </button>
                        <div className="info-grid">
                            <div className="info-item">
                                <span className="info-label">Total Quantity:</span>
                                <span className="info-value">{product.quantity}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Available Quantity:</span>
                                <span className="info-value">{product.quantityAvailable}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Committed Quantity:</span>
                                <span className="info-value">{product.commitedQuantity}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Weight:</span>
                                <span className="info-value">{product.weight} {product.weightUnit}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Price:</span>
                                <span className="info-value">${product.price.toFixed(2)}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Managed By Serial:</span>
                                <span className="info-value">{product.managedBySerial ? 'Yes' : 'No'}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Category:</span>
                                <span className="info-value">{category ? category.name : 'Loading...'}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Warehouse:</span>
                                <span className="info-value">{warehouse ? warehouse.name : 'Loading...'}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Department:</span>
                                <span className="info-value">{department ? department.name : 'Loading...'}</span>
                            </div>
                            {product.militaryOrDualUse && (
                            <div className="info-item">
                                <span className="info-label">Military/Dual Use:</span>
                                <span className="info-value">{product.militaryOrDualUse}</span>
                            </div>
                            )}
                            {product.hsCode && (
                            <div className="info-item">
                                <span className="info-label">HS Code:</span>
                                <span className="info-value">{product.hsCode}</span>
                            </div>
                            )}
                            {product.createdAt && (
                            <div className="info-item">
                                <span className="info-label">Created:</span>
                                <span className="info-value">{formatDateTime(product.createdAt)}</span>
                            </div>
                            )}
                        </div>
                    </div>

                    {/* Serial Numbers Section */}
                    {product.managedBySerial && (
                        <div className="serial-numbers-section">
                            <h3>Serial Numbers</h3>
                            <div className="serial-numbers-container">
                                <div className="serial-numbers-column">
                                    <h4>All Serial Numbers ({product.serialNumber.length})</h4>
                                    <div className="serial-numbers-list">
                                        {[...product.serialNumber].sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' })).map((serial) => {
                                            // Find the corresponding note and status from the product's serialNumberNotes
                                            const serialNoteObj = product.serialNumberNotes?.find(sn => sn.value === serial);
                                            const serialNote = serialNoteObj?.notes || '';
                                            const functionalStatus = serialNoteObj?.functionalStatus || '';
                                            return (
                                                <SerialNumberBadge 
                                                    key={serial}
                                                    serialNumber={serial} 
                                                    note={serialNote}
                                                    functionalStatus={functionalStatus}
                                                    onGoToUpdatePage={handleGoToUpdatePage}
                                                />
                                            );
                                        })}
                                    </div>
                                </div>
                                <div className="serial-numbers-column">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                        <h4>
                                            Available Serial Numbers ({product.serialNumberAvailable.length})
                                        </h4>
                                    </div>
                                    <div className="serial-numbers-list">
                                        {[...product.serialNumberAvailable].sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' })).map((serial) => {
                                            const serialNoteObj = product.serialNumberNotes?.find(sn => sn.value === serial);
                                            const serialNote = serialNoteObj?.notes || '';
                                            const functionalStatus = serialNoteObj?.functionalStatus || '';
                                            return (
                                                <SerialNumberBadge 
                                                    key={serial}
                                                    serialNumber={serial} 
                                                    note={serialNote}
                                                    functionalStatus={functionalStatus}
                                                    onGoToUpdatePage={handleGoToUpdatePage}
                                                />
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Related Orders Section */}
                    {relatedOrders.length > 0 && (
                        <div className="related-orders-section">
                            <h3>Related Orders</h3>
                            <table className="related-orders-table">
                                <thead>
                                    <tr>
                                        <th>Order Number</th>
                                        <th>Status</th>
                                        <th>Quantity</th>
                                        <th>Destination</th>
                                        <th>Dates</th>
                                        {product.managedBySerial && <th>Serial Numbers</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {relatedOrders.map(order => {
                                        const productInOrder = order.products.find(p => p.product === productId);
                                        return (
                                            <tr key={order._id}>
                                                <td>{order.orderNumber}</td>
                                                <td>
                                                    <OrderStatusBadge status={order.orderStatus} />
                                                </td>
                                                <td>{productInOrder?.quantity || 0}</td>
                                                <td>{order.destination}</td>
                                                <td>
                                                    {new Date(order.fromDate).toLocaleDateString('he-IL')} -
                                                    {new Date(order.untilDate).toLocaleDateString('he-IL')}
                                                </td>
                                                {product.managedBySerial && (
                                                   <td>
                                                        {productInOrder?.serialNumbers ?
                                                            <div className="serial-numbers-cell">
                                                                {productInOrder.serialNumbers.map((serial, index) => {
                                                                    const serialNoteObj = product.serialNumberNotes?.find(sn => sn.value === serial);
                                                                    return (
                                                                        <SerialNumberBadge
                                                                            key={`${order._id}-${serial}-${index}`}
                                                                            serialNumber={serial}
                                                                            note={serialNoteObj?.notes || ''}
                                                                            functionalStatus={serialNoteObj?.functionalStatus || ''}
                                                                            onGoToUpdatePage={handleGoToUpdatePage}
                                                                        />
                                                                    );
                                                                })}
                                                            </div>
                                                        : 'N/A'}
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Missing Items Section */}
                    {missingItems.length > 0 && (
                        <div className="missing-items-section">
                            <h3 style={{ color: '#dc3545' }}>⚠️ Missing Items</h3>
                            <table className="related-orders-table">
                                <thead>
                                    <tr>
                                        <th>Order Number</th>
                                        <th>Order Name</th>
                                        <th>Requested By</th>
                                        <th>Location</th>
                                        <th>Order Date</th>
                                        {product.managedBySerial ? <th>Missing Serial Numbers</th> : <th>Missing Quantity</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {missingItems.map((item, index) => (
                                        <tr key={`${item.orderNumber}-${index}`} style={{ backgroundColor: '#fff3cd' }}>
                                            <td style={{ fontWeight: 'bold' }}>{item.orderNumber}</td>
                                            <td>{item.orderName}</td>
                                            <td>{item.userName}</td>
                                            <td>{item.destination}</td>
                                            <td>
                                                {new Date(item.fromDate).toLocaleDateString('he-IL')} -
                                                {new Date(item.untilDate).toLocaleDateString('he-IL')}
                                            </td>
                                            <td>
                                                {item.managedBySerial ? (
                                                    item.missingSerialNumbers.length > 0 ? (
                                                        <div className="serial-numbers-cell">
                                                            {item.missingSerialNumbers.map((serial, serialIndex) => {
                                                                const serialNoteObj = product.serialNumberNotes?.find(sn => sn.value === serial);
                                                                return (
                                                                    <SerialNumberBadge
                                                                        key={`missing-${item.orderNumber}-${serial}-${serialIndex}`}
                                                                        serialNumber={serial}
                                                                        note={serialNoteObj?.notes || ''}
                                                                        functionalStatus={serialNoteObj?.functionalStatus || ''}
                                                                        onGoToUpdatePage={handleGoToUpdatePage}
                                                                    />
                                                                );
                                                            })}
                                                        </div>
                                                    ) : 'N/A'
                                                ) : (
                                                    <span style={{ color: '#dc3545', fontWeight: 'bold' }}>{item.missingQuantity}</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                {/* Related Faulty Items Section */}
                {relatedFaultyItems.length > 0 && (
                    <div className="related-faulty-items-section">
                        <h3>Related Faulty Items</h3>
                        <table className="related-orders-table"> 
                            <thead>
                                <tr>
                                    <th>Fault Description</th>
                                    <th>Severity</th>
                                    <th>Status</th>
                                    {product.managedBySerial ? <th>Serial Number</th> : <th>Quantity</th>}
                                    <th>Reported At</th>
                                    <th>Actions</th>

                                </tr>
                            </thead>
                            <tbody>
                                {relatedFaultyItems.map(item => (
                                    <tr key={item._id}>
                                        <td>{item.faultDescription}</td>
                                        <td>{item.severity}</td>
                                        <td>{item.status}</td>
                                        <td>
                                            {item.managedBySerial
                                                ? (
                                                    <div > {/* Optional: for styling multiple badges */}
                                                        {item.faultySerialNumbers && item.faultySerialNumbers.length > 0 ? (
                                                            item.faultySerialNumbers.map((serial, index) => (
                                                                <SerialNumberBadge key={`${item._id}-faulty-${serial}-${index}`} serialNumber={serial} isRemovable={false} />
                                                            ))
                                                        ) : 'N/A'}
                                                    </div>
                                                ) : item.faultyQuantity}
                                        </td>
                                        <td>{formatDateTime(item.createdAt)}</td>
                                        <td className="actionsCell">
                                            {item.status !== 'Repaired' && (
                                                <button
                                                    onClick={() => handleMarkAsRepaired(item._id)}
                                                    className={`${"actionButton"} ${"repairButton"}`}
                                                    title="Mark as Repaired"
                                                >
                                                    <RiToolsLine size={20} />
                                                </button>
                                            )}
                                            <button
                                                onClick={() => handleEditFaultyItem(item)}
                                                className={`${"actionButton"} ${"editButton"}`}
                                                title="Edit Faulty Item"
                                            >
                                                <RiEdit2Line size={20} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                    {/* Notes Section */}
                    {product.notes && (
                        <div className="notes-section">
                            <h3>Notes</h3>
                            <p>{product.notes}</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProductDetailsModal;
